import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import tinycolor, { Instance as TinyColor } from 'tinycolor2';
import useStyles from './styles'
import { Box, Button, Divider, Stack } from "@mui/material";
import HueSlider from "../hueSlider/hueSlider";

export type CustomColorSelectorProps = {
    value: TinyColor,
    onColorSelect: (color: TinyColor) => void,
    disableSwatches?: boolean
    onAddSwatch?: (color: TinyColor) => void,
    onClose: () => void
}

const CustomColorSelector: React.FC<CustomColorSelectorProps> = ({value, disableSwatches, onColorSelect, onAddSwatch, onClose}) => {
    const spaceContainer = useRef<HTMLDivElement>(null)
    const containerBounds = useRef<DOMRect>()
    const originalValue = useRef(value.toHsv())
    const [active, setActive] = useState(false)
    const hsv = value.toHsv();
    const [currentHue, setCurrentHue] = useState(hsv.h)
    const baseColor = useMemo(() => tinycolor({
        h: currentHue,
        s: 1,
        v: 1
    }), [currentHue])
    const {classes} = useStyles({color: baseColor})
    const [position, setPosition] = useState<{x: number, y: number}>({x: value.toHsv().s, y: 1 - value.toHsv().v})
    // useEffect(() => {
    //     previousValue.current = value.toHsv()
    // }, [value])


    const onHueChange = useCallback((hue: number) => {
        setCurrentHue(hue)
    }, [])

    useEffect(() => {
        // console.log("color: setting", hue.current, position.x, position.y)
        onColorSelect(tinycolor({
            h: baseColor.toHsv().h,
            s: position.x,
            v: 1 - position.y,
            a: originalValue.current.a
        }))
    }, [position, baseColor])

    const onMouseDown = useCallback(() => {
        if (!spaceContainer.current) return;
        setActive(true)
        containerBounds.current = spaceContainer.current.getBoundingClientRect()
    }, [])

    
    const onMouseMove = useCallback((event: MouseEvent) => {
        if (!containerBounds.current) return;
        const { clientX, clientY} = event;
        const { left, right, top, bottom } = containerBounds.current;
        const width = right - left;
        const height = bottom - top;

        const deltaX = clientX - left;
        const deltaY = clientY - top;

        setPosition({
            x: (deltaX < 0 ? 0 : Math.min(deltaX, width))/width,
            y: (deltaY < 0 ? 0 : Math.min(deltaY, height))/height
        })
    }, [])

    const onMouseUp = useCallback(() => {
        setActive(false)
        containerBounds.current = undefined
    }, [])

    useEffect(() => {
        if (active) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp)
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp)
        }
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp)
        }
    }, [active])

    const onCancelClick = useCallback(() => {
        onColorSelect(tinycolor(originalValue.current))
        onClose();
    }, [onColorSelect, onClose]);

    const onAddClick = useCallback(() => {
        onAddSwatch && onAddSwatch(baseColor)
    }, [baseColor, onAddSwatch]);

    return <Box>
        <Stack direction="row" gap={0.75}>
            <div className={classes.colorSpace} ref={spaceContainer} onMouseDown={onMouseDown}>
                <div className={classes.colorPointer} style={{top: `${(position?.y??0)*100}%`, left: `${(position?.x??0)*100}%`}}></div>
            </div>
            <div className={classes.hueSliderContainer}>
                <HueSlider hue={currentHue} onHueChange={onHueChange} />
            </div>
        </Stack>
        <Stack direction="row" sx={{marginTop: 1}} justifyContent="space-between">
            <Button variant="outlined" size="small"  sx={{minWidth: '1px', padding: '5px', lineHeight: '1'}} onClick={onCancelClick}>Cancel</Button>
            <Button variant="contained" size="small" sx={{minWidth: '1px', paddingY: '5px', lineHeight: '1'}} onClick={onAddClick}>Add</Button>
        </Stack>
    </Box>;
}


export default memo(CustomColorSelector)